import styles from './FJGenerateInfo.module.less';
import CopyIcon from '../../../../../assets/svg/prompt-copy.svg?react';
import FJImageList from './FJImageList.jsx';
import FJMoreTool from './FJMoreTool.jsx';
import FJExpandableText from './FJExpandableText.jsx';
import FJLocalStore from '../../../../../store/FJLocalStore.js';
import { useMemo } from 'react';
import FJUtil from '../../../../../util/FJUtil.js';
import FJMessageStore from '../../../../../store/FJMessageStore.js';
import {
    AI_GENERATOR_VIDEO_TAB_TYPE,
    IMAGE_UPLOAD_MODES,
} from '../../../../../config/dataConfig/FJAIVideoGeneratorData.js';
import FJRequirementsUtil from '../../../../../util/FJRequirementsUtil.js';
import FJAIVideoGeneratorConfig from '../../../../../config/appConfig/FJAIVideoGeneratorConfig.js';
import PropTypes from 'prop-types';
import { VIDEO_EFFECT_STYLE_LIST } from '../../../../../config/dataConfig/FJVideoEffectData.js';
import { RELEASE } from '../../../../../constants/FJEvnConstant.js';
import FJToolTip from '../../../../../common/components/FJToolTip/FJToolTip.jsx';
import FJAppConstant from '../../../../../constants/FJAppConstant.js';
import FJCommunityUtil from '../../../../../util/FJCommunityUtil.js';

const environment = import.meta.env.MODE;
const isRelease = environment !== RELEASE;

const jobTypeJumpHref = {};
jobTypeJumpHref[3] = {
    href: isRelease ? 'apply/image-to-image/' : 'ai/image-to-image/',
    appType: FJAppConstant.IMAGE_TO_IMAGE,
};
jobTypeJumpHref[6] = {
    href: isRelease ? 'apply/text-to-image/' : 'ai/text-to-image/',
    appType: FJAppConstant.TEXT_TO_IMAGE,
};
jobTypeJumpHref[36] = {
    href: isRelease ? 'apply/photo-editor/' : 'ai/photo-editor/',
    appType: FJAppConstant.AI_PHOTO_EDITOR,
};
jobTypeJumpHref[28] = {
    href: isRelease ? 'apply/video-generator/video-effect/' : 'ai/video-generator/video-effect/',
    appType: FJAppConstant.AI_VIDEO_EFFECT,
};
jobTypeJumpHref[14] = {
    href: isRelease ? 'apply/video-generator/' : 'ai/video-generator/',
    appType: FJAppConstant.AI_VIDEO_GENERATOR,
};

const FJGenerateInfo = props => {
    const { info } = props;
    const {
        originalUrl,
        textToImagePrompt,
        prompt,
        width,
        height,
        generationType,
        ratio,
        singleImage, // 视频生成 单张图片
        startImage, // 视频生成 开始图片
        endImage, // 视频生成 结束图片
        multiImage, // 视频生成 多张图片
        currentImageMode, // 视频生成 当前图片模式
        createTime,
        model,
        style,
        jobType,
        originalImages = [],
        quality,
        workType,
        orientation,
    } = info;

    const videoEffectStyleItem = useMemo(() => {
        if (jobType === FJAIVideoGeneratorConfig.videoEffectJobType) {
            return VIDEO_EFFECT_STYLE_LIST.find(item => item.category === info?.effectInfo?.effect_scene) || {};
        }
        return {};
    }, [jobType, info?.effectInfo?.effect_scene]);

    const imgList = useMemo(() => {
        if (originalImages && originalImages?.length > 0) {
            return originalImages;
        }
        switch (jobType) {
            case 6: {
                // 6 FJAppConstant.TEXT_TO_IMAGE
                if (info.referenceImages?.length > 0) {
                    return info.referenceImages;
                } else {
                    return [];
                }
            }
            case 3: {
                // 3 FJAppConstant.IMAGE_TO_IMAGE
                return [originalUrl];
            }

            case 36: {
                // 36 FJAppConstant.AI_PHOTO_EDITOR
                if (info.inputImages?.length > 0) {
                    return info.inputImages;
                } else if (info.inputImage) {
                    return [info.inputImage];
                } else {
                    return [];
                }
            }

            case 28: {
                if (info?.effectInfo?.effectOriginalUrlList) {
                    return info?.effectInfo?.effectOriginalUrlList;
                } else {
                    return [];
                }
            }
            case 14: {
                // 14 FJAppConstant.IMAGE_TO_VIDEO
                let list = [];
                if (currentImageMode === IMAGE_UPLOAD_MODES.START_END) {
                    if (startImage) {
                        list.push(startImage);
                    }
                    if (endImage) {
                        list.push(endImage);
                    }
                } else {
                    if (singleImage) {
                        list.push(singleImage);
                    }
                    if (startImage) {
                        list.push(startImage);
                    }
                    if (multiImage && multiImage?.length > 0) {
                        list = multiImage.filter(item => !!item);
                    }
                    /* 兼容旧版：当没有新图片数据时，使用 originalUrl */
                    if (!singleImage && !startImage && !multiImage && originalUrl) {
                        list.push(originalUrl);
                    }
                }
                return list;
            }
            default:
                return [];
        }
    }, [
        info.inputImages,
        info.referenceImages,
        info?.effectInfo?.effectOriginalUrlList,
        currentImageMode,
        startImage,
        endImage,
        singleImage,
        multiImage,
        originalUrl,
        jobType,
        originalImages,
    ]);

    const infoList = useMemo(() => {
        const infoArray = [];

        switch (jobType) {
            case 6: {
                // FJAppConstant.TEXT_TO_IMAGE
                infoArray.push(FJLocalStore._('ai-text-to-image'));
                model && infoArray.push(FJLocalStore._(model));
                quality && infoArray.push(FJLocalStore._(quality));
                style && infoArray.push(FJLocalStore._(style));
                ratio && infoArray.push(FJUtil.floor(ratio));
                width && height && infoArray.push(`${width} * ${height} px`);
                break;
            }
            case 3: {
                // FJAppConstant.IMAGE_TO_IMAGE
                infoArray.push(FJLocalStore._('ai-image-to-image'));
                const styleItem = FJRequirementsUtil.getImgToImgDataStyleItem(style);
                const styleItemName = styleItem ? styleItem.name : style;
                infoArray.push(FJLocalStore._(styleItemName));
                ratio && infoArray.push(FJUtil.floor(ratio));
                width && height && infoArray.push(`${width} * ${height} px`);
                break;
            }

            case 36: {
                // 36 FJAppConstant.AI_PHOTO_EDITOR
                infoArray.push(FJLocalStore._('ai-photo-editor'));
                model && infoArray.push(FJLocalStore._(model));
                quality && infoArray.push(FJLocalStore._(quality));
                ratio && infoArray.push(FJUtil.floor(ratio));
                width && height && infoArray.push(`${width} * ${height} px`);
                break;
            }

            case 28: {
                infoArray.push(FJLocalStore._(AI_GENERATOR_VIDEO_TAB_TYPE.AI_EFFECTS_VIDEO));
                if (videoEffectStyleItem && videoEffectStyleItem?.name) {
                    infoArray.push(FJLocalStore._(videoEffectStyleItem.name));
                }
                width && height && infoArray.push(`${width} * ${height} px`);
                break;
            }
            case 14: {
                if (generationType === 'iv') {
                    infoArray.push(FJLocalStore._('ai-image-to-video'));
                } else if (generationType === 'tv') {
                    infoArray.push(FJLocalStore._('ai-text-to-video'));
                }
                model && infoArray.push(FJLocalStore._(model));
                width && height && infoArray.push(`${width} * ${height} px`);
                orientation &&
                    infoArray.push(
                        FJLocalStore._(orientation === 'video' ? 'based-on-the-video' : 'based-on-the-image'),
                    );
                break;
            }
            default:
        }

        createTime && infoArray.push(FJUtil.convertToCurrentTimezone(createTime));

        return infoArray;
    }, [
        jobType,
        createTime,
        model,
        style,
        ratio,
        width,
        height,
        videoEffectStyleItem,
        generationType,
        quality,
        orientation,
    ]);

    const _copy = () => {
        FJUtil.copyText(textToImagePrompt || prompt);
        FJMessageStore.addMessage(FJLocalStore._('copied-successfully'));
    };

    const _onJumpTool = index => {
        let url = null;
        if (index === 0) {
            let appType = '';
            if (jobType === 14) {
                url = jobTypeJumpHref[jobType]?.href;
                if (generationType === 'iv') {
                    url = url + 'image-to-video';
                    appType = FJAppConstant.IMAGE_TO_VIDEO;
                } else if (generationType === 'tv') {
                    url = url + 'text-to-video';
                    appType = FJAppConstant.TEXT_TO_VIDEO;
                }
            } else {
                url = jobTypeJumpHref[jobType]?.href;
                appType = jobTypeJumpHref[jobType]?.appType;
            }
            // window.open(FJUtil.getCurrentUrl() + url, '_blank');
            FJCommunityUtil.onPreviewJumpToApp(info, { currentAppType: appType, targetAppType: appType, href: url });
            FJUtil.eaLog('ai preview tools click', {
                tool: FJUtil.getToolName(appType),
                'current tools': FJUtil.getToolName(appType),
                'ai page': FJCommunityUtil.getCommunityPageName({ workType }),
            });
        }
    };

    return (
        <div className={styles.box}>
            <div className={styles.scroller}>
                <div style={{ flex: 1 }}>
                    {(textToImagePrompt || prompt) && (
                        <>
                            <div className={styles.item_header}>
                                <div className={styles.title}>{FJLocalStore._('prompt')}</div>
                                <FJToolTip title={'copy'}>
                                    <div className={styles.copy_btn} onClick={_copy}>
                                        <CopyIcon />
                                    </div>
                                </FJToolTip>
                            </div>
                            <FJExpandableText maxLines={2} lineHeight={24}>
                                {textToImagePrompt || prompt}
                            </FJExpandableText>
                        </>
                    )}

                    {imgList.length > 0 && (
                        <>
                            <div className={styles.title} style={{ marginTop: 22 }}>
                                {FJLocalStore._(imgList.length === 1 ? 'original-image' : 'original-images')}
                            </div>
                            <FJImageList list={imgList} />
                        </>
                    )}

                    <div className={styles.line} />
                    <div className={styles.tag_box}>
                        {infoList.map((item, index) => {
                            return (
                                <div
                                    className={styles.tag}
                                    key={index}
                                    onClick={() => {
                                        _onJumpTool(index);
                                    }}
                                >
                                    {item}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <FJMoreTool jobType={jobType} generationType={generationType} workType={workType} info={info} />
            </div>
        </div>
    );
};

FJGenerateInfo.propTypes = {
    info: PropTypes.object.isRequired,
    videoEffectStyleList: PropTypes.array.isRequired,
};
export default FJGenerateInfo;
